import React, { useState, useEffect, useRef } from "react";
import productHandler from "./../Functions/product-handler";
import { formatPrice } from "../Functions";
// import { async } from 'q';
import { toast } from "react-toastify";
import productPlaceholder from "../Assets/images/product_placeholder.png";
import SpinnerLoader from "../Common/SpinnerLoader";
import ModalHeader from "../Common/ModalHeader";

// Small inline icons so the Add to Cart flow does not depend on external assets.
const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M12 7v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="16.5" r="1.1" fill="currentColor" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Container = ({
  multiProducts,
  pdf,
  cartCounterHandler,
  buttonType,
  collectionName,
  collectionDescription,
  handleModal,
}) => {
  const [collectionproductloader] = useState(false);
  const [showMultiProducts] = useState(multiProducts);
  const [addedRelated, setAddedRelated] = useState({});
  // Tracks which main products were added, keyed by sku_code
  const [addedMain, setAddedMain] = useState({});
  // Which screen of the Add to Cart flow is visible: the product list or the
  // mandatory-products drill-down.
  const [screen, setScreen] = useState("main");
  const [mandatoryFor, setMandatoryFor] = useState(null);
  // Open state for the "Related Products" accordions, keyed by product sku_code.
  const [relatedOpen, setRelatedOpen] = useState({});
  // Collection view: toggle between adding the whole collection vs. individual
  // products, the "Contains Products" accordion, and whether the collection was
  // added as a whole.
  const [individualMode, setIndividualMode] = useState(false);
  const [containsOpen, setContainsOpen] = useState(true);
  const [collectionAdded, setCollectionAdded] = useState(false);
  const cart = useRef();

  useEffect(() => {
    cart.current = new productHandler(pdf);
  }, [pdf]);

  const addToCart = async (
    sku_code,
    name,
    pQuantity,
    tag,
    isMandatory,
    ManProducts = [],
    parentId,
    fullItem = null,
    isRelated = false,
    inlineMainKey = null
  ) => {
    try {
      // Add main product
      const res = await cart.current.AddToCart(
        sku_code,
        pQuantity,
        tag,
        false,
        0
      );

      if (!res?.data?.success) {
        toast.error("Something went wrong!", {
          className: "toast-style",
        });
        return;
      }

      if (isRelated) {
        const relatedKey = Array.isArray(sku_code) ? sku_code[0] : sku_code;
        setAddedRelated((prev) => ({ ...prev, [relatedKey]: true }));
      } else {
        toast.success("Added to Cart successfully", {
          className: "toast-style",
        });
      }

      // Optional: use newly created cart item id
      const addedCartId = res?.data?.data?._id || parentId;

      // Add mandatory products
      if (ManProducts?.length > 0) {
        await Promise.all(
          ManProducts.map(async (m) => {
            try {
              await cart.current.AddToCart(
                [m.sku_code],
                m.quantity,
                "",
                true,
                addedCartId
              );
            } catch (err) {
              console.error(
                `Failed to add mandatory product: ${m.sku_code}`,
                err
              );
            }
          })
        );
      }

      // Hit after ALL products are added
      await cartCounterHandler.fetchCartCounterData();

      if (inlineMainKey) {
        setAddedMain((prev) => ({ ...prev, [inlineMainKey]: true }));
      }
    } catch (error) {
      console.error(error);

      toast.error("Something went wrong!", {
        className: "toast-style",
      });
    }
  };

  const handleAllCollectionCart = async () => {
    try {
      for (const x in multiProducts) {
        const res = await cart.current.AddToCart(
          [multiProducts[x]?.sku_code],
          multiProducts[x]?.pQunatity,
          multiProducts[x]?.tag
        );
        const error = res.data.success;

        if (!error) {
          toast.success("something went wrong!", {
            className: "toast-style",
          });
        } else {
          if (x === "0") {
            toast.success("collection added to cart successfully", {
              className: "toast-style",
            });
          }
        }
      }
      cartCounterHandler.fetchCartCounterData();
    } catch (error) {
      toast.success("something went wrong!", {
        className: "toast-style",
      });
    }
  };

  // Total for a single product (its price * qty + its mandatory products).
  const getGrandTotal = (item) => {
    const mainQty = item?.pQunatity ? item.pQunatity : 1;
    return (
      Number(item?.product_price || 0) * mainQty +
      (item?.MandatoryProducts || []).reduce(
        (acc, m) =>
          acc + Number(m?.product_price || 0) * Number(m?.quantity || 1),
        0
      )
    );
  };

  const closeModal = () => handleModal();

  const openMandatory = (item) => {
    setMandatoryFor(item);
    setScreen("mandatory");
  };

  const backToMain = () => {
    setScreen("main");
    setMandatoryFor(null);
  };

  const toggleRelated = (id) => {
    setRelatedOpen((prev) => ({ ...prev, [id]: !isRelatedOpen(id) }));
  };

  // Related Products accordions are collapsed by default.
  const isRelatedOpen = (id) =>
    id in relatedOpen ? relatedOpen[id] : false;

  const isMultiProduct = (showMultiProducts || []).length > 1;
  const singleItem = showMultiProducts?.[0];
  const addedCount =
    Object.keys(addedMain).length + Object.keys(addedRelated).length;

  const renderMandatoryPill = (item) => {
    const count = item?.MandatoryProducts?.length || 0;
    if (count <= 0) return null;
    return (
      <button
        type="button"
        className="atcMandatoryPill"
        onClick={() => openMandatory(item)}
      >
        <span className="atcMandatoryPillIcon">
          <InfoIcon />
        </span>
        Includes +{count} Mandatory Products
      </button>
    );
  };

  const renderRelatedAccordion = (title, items, id) => {
    if (!items || items.length === 0) return null;
    const open = isRelatedOpen(id);
    return (
      <div className="atcAccordion" key={`rel-${id}`}>
        <div
          className="atcAccordionHeader"
          onClick={() => toggleRelated(id)}
        >
          <span className="atcAccordionTitle">
            {title}({items.length})
          </span>
          <span className={`atcAccordionChevron ${open ? "open" : ""}`}>
            <ChevronIcon />
          </span>
        </div>
        {open && (
          <div className="atcAccordionBody">
            {items.map((c, i) => {
              const isAdded = addedRelated[c.sku_code];
              return (
                <div className="atcRelatedItem" key={`${id}-${i}`}>
                  <div className="atcRelatedImg">
                    <img
                      src={c?.product_image || productPlaceholder}
                      onError={(e) => {
                        e.target.src = productPlaceholder;
                      }}
                      alt={c?.product_name}
                    />
                  </div>
                  <div className="atcRelatedInfo">
                    <h4>{c?.product_name}</h4>
                    <p className="atcSku">{c?.sku_code}</p>
                    <p className="atcDesc">{c?.product_description}</p>
                  </div>
                  <div className="atcRelatedPrice">
                    &#8377;{formatPrice(c?.product_price)}
                    <span className="atcUnitSuffix"> /unit</span>
                  </div>
                  <button
                    type="button"
                    className={`atcAddToCartBtn small${isAdded ? " added" : ""}`}
                    disabled={isAdded}
                    onClick={() =>
                      addToCart(
                        [c.sku_code],
                        c.product_name,
                        c?.pQunatity ? c.pQunatity : 1,
                        "",
                        false,
                        [],
                        null,
                        c,
                        true
                      )
                    }
                  >
                    {isAdded ? "Added ✓" : "Add to Cart"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ---- Collection flow (buttonType === "3") ----
  if (buttonType === "3") {
    if (collectionproductloader) return <SpinnerLoader />;
    if (!(showMultiProducts && showMultiProducts.length > 0))
      return <h3 style={{ textAlign: "center" }}> No Items</h3>;

    const productCount = showMultiProducts.length;
    const collectionTotal = showMultiProducts.reduce(
      (acc, p) =>
        acc + Number(p?.product_price || 0) * Number(p?.pQunatity || 1),
      0
    );
    const gridImages = showMultiProducts.slice(0, 4);
    const collectionCount = collectionAdded
      ? productCount
      : Object.keys(addedMain).length;

    const addWholeCollection = async () => {
      await handleAllCollectionCart();
      setCollectionAdded(true);
    };

    return (
      <div className="atcModal">
        <ModalHeader title="Add to Cart" onClose={closeModal} />

        <div className="atcBody">
          <div className="atcSingleMain">
            <div className="atcSingleImg atcCollectionImg">
              <div className="atcCollectionGrid">
                  <img
                    src={productPlaceholder}
                    alt="Collection"
                  />
              </div>
            </div>
            <div className="atcSingleInfo">
              <h3 className="atcName">{collectionName || "Collection"}</h3>
              <p className="atcSku">{productCount} products</p>
              {collectionDescription && (
                <p className="atcDesc">{collectionDescription}</p>
              )}
              <p className="atcUnitPrice">
                &#8377;{formatPrice(collectionTotal)}
                <span className="atcUnitSuffix"> /unit</span>
              </p>
              {!individualMode && (
                <button
                  type="button"
                  className={`atcAddToCartBtn${collectionAdded ? " added" : ""}`}
                  disabled={collectionAdded}
                  onClick={addWholeCollection}
                >
                  {collectionAdded ? "Added ✓" : "Add to Cart"}
                </button>
              )}
            </div>
          </div>

          <div className="atcToggleRow">
            <button
              type="button"
              role="switch"
              aria-checked={individualMode}
              className={`atcToggle${individualMode ? " on" : ""}`}
              onClick={() => setIndividualMode((v) => !v)}
            >
              <span className="atcToggleKnob" />
            </button>
            <span className="atcToggleLabel">Add products individually</span>
          </div>

          <div className="atcAccordion">
            <div
              className="atcAccordionHeader"
              onClick={() => setContainsOpen((v) => !v)}
            >
              <span className="atcAccordionTitle">
                Contains Products({productCount})
              </span>
              <span
                className={`atcAccordionChevron ${containsOpen ? "open" : ""}`}
              >
                <ChevronIcon />
              </span>
            </div>
            {containsOpen && (
              <div className="atcAccordionBody">
                {showMultiProducts.map((item, index) => {
                  const isAdded =
                    addedMain[item.sku_code] || collectionAdded;
                  return (
                    <div className="atcRelatedItem" key={index}>
                      <div className="atcRelatedImg">
                        <img
                          src={item?.product_image || productPlaceholder}
                          onError={(e) => {
                            e.target.src = productPlaceholder;
                          }}
                          alt={item?.product_name}
                        />
                      </div>
                      <div className="atcRelatedInfo">
                        <h4>{item?.product_name}</h4>
                        <p className="atcSku">{item?.sku_code}</p>
                        <p className="atcDesc">{item?.product_description}</p>
                      </div>
                      <div className="atcRelatedPrice">
                        &#8377;{formatPrice(item?.product_price)}
                      </div>
                      {individualMode && (
                        <button
                          type="button"
                          className={`atcAddToCartBtn small${
                            isAdded ? " added" : ""
                          }`}
                          disabled={isAdded}
                          onClick={() =>
                            addToCart(
                              [item.sku_code],
                              item.product_name,
                              item?.pQunatity ? item.pQunatity : 1,
                              item?.tag,
                              false,
                              item?.MandatoryProducts || [],
                              item.products_id,
                              item,
                              false,
                              item.sku_code
                            )
                          }
                        >
                          {isAdded ? "Added ✓" : "Add to Cart"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="atcFooter">
          <span className="atcItemsAdded">
            {collectionCount} item{collectionCount === 1 ? "" : "s"} added
          </span>
          <button type="button" className="atcDoneBtn" onClick={closeModal}>
            Done
          </button>
        </div>
      </div>
    );
  }

  // ---- Add to Cart flow (single / multi product) ----
  if (collectionproductloader) return <SpinnerLoader />;
  if (!(showMultiProducts && showMultiProducts.length > 0))
    return <h3 style={{ textAlign: "center" }}> No Items</h3>;

  // Mandatory products drill-down screen.
  if (screen === "mandatory" && mandatoryFor) {
    const mList = mandatoryFor.MandatoryProducts || [];
    return (
      <div className="atcModal">
        <ModalHeader
          title={`Mandatory Products(${mList.length})`}
          onBack={backToMain}
          onClose={closeModal}
        />

        <div className="atcBody">
          <div className="atcMandatoryList">
            {mList.map((m, i) => (
              <div className="atcMandatoryItem" key={i}>
                <div className="atcMandatoryImg">
                  <img
                    src={m?.product_image || productPlaceholder}
                    onError={(e) => {
                      e.target.src = productPlaceholder;
                    }}
                    alt={m?.product_name}
                  />
                </div>
                <div className="atcMandatoryInfo">
                  <h4>{m?.product_name}</h4>
                  <p className="atcSku">{m?.sku_code}</p>
                  <p className="atcDesc">{m?.product_description}</p>
                </div>
                <div className="atcMandatoryPrice">
                  <span className="amt">&#8377;{formatPrice(m?.product_price)}</span>
                  <span className="suffix"> /unit</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="atcFooter atcFooterEnd">
          <button type="button" className="atcDoneBtn" onClick={backToMain}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="atcModal">
      <ModalHeader title="Add to Cart" onClose={closeModal} />

      <div className="atcBody">
        {isMultiProduct ? (
          <>
            <div className="atcMultiList">
              {showMultiProducts.map((item, index) => {
                const mainQty = item?.pQunatity ? item.pQunatity : 1;
                const isAdded = addedMain[item.sku_code];
                const hasMandatory = item?.MandatoryProducts?.length > 0;
                return (
                  <React.Fragment key={index}>
                    {/* {index > 0 && <hr className="atcDivider" />} */}
                    <div className="atcMultiRow">
                      <div className="atcMultiImg">
                        <img
                          src={item?.product_image || productPlaceholder}
                          onError={(e) => {
                            e.target.src = productPlaceholder;
                          }}
                          alt={item?.product_name}
                        />
                      </div>
                      <div className="atcMultiInfo">
                        <h3 className="atcName">{item?.product_name}</h3>
                        <p className="atcSku">{item?.sku_code}</p>
                        <p className="atcDesc">{item?.product_description}</p>
                        <div className="atcPriceRow">
                          <p className="atcUnitPrice">
                            &#8377;{formatPrice(getGrandTotal(item))}
                            <span className="atcUnitSuffix"> /unit</span>
                          </p>
                          {hasMandatory && renderMandatoryPill(item)}
                        </div>
                        <button
                          type="button"
                          className={`atcAddToCartBtn${isAdded ? " added" : ""}`}
                          disabled={isAdded}
                          onClick={() =>
                            addToCart(
                              [item.sku_code],
                              item.product_name,
                              mainQty,
                              "",
                              false,
                              item?.MandatoryProducts || [],
                              item.products_id,
                              item,
                              false,
                              item.sku_code
                            )
                          }
                        >
                          {isAdded ? "Added ✓" : "Add to Cart"}
                        </button>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Related products accordion per product */}
            {showMultiProducts.map((item) =>
              renderRelatedAccordion(
                `Related Products of ${item?.product_name}`,
                item?.child_sku,
                item?.sku_code
              )
            )}
          </>
        ) : (
          <>
            <div className="atcSingleMain">
              <div className="atcSingleImg">
                <img
                  src={singleItem?.product_image || productPlaceholder}
                  onError={(e) => {
                    e.target.src = productPlaceholder;
                  }}
                  alt={singleItem?.product_name}
                />
              </div>
              <div className="atcSingleInfo">
                <h3 className="atcName">{singleItem?.product_name}</h3>
                <p className="atcSku">{singleItem?.sku_code}</p>
                <p className="atcUnitPrice">
                  &#8377;{formatPrice(singleItem?.product_price)}
                  <span className="atcUnitSuffix"> /unit</span>
                </p>
                <p className="atcDesc">{singleItem?.product_description}</p>

                {singleItem?.MandatoryProducts?.length > 0 && (
                  <>
                    {renderMandatoryPill(singleItem)}
                    <p className="atcTotalPrice">
                      &#8377;{formatPrice(getGrandTotal(singleItem))}
                      <span className="atcUnitSuffix"> /unit</span>
                    </p>
                  </>
                )}

                <button
                  type="button"
                  className={`atcAddToCartBtn${
                    addedMain[singleItem?.sku_code] ? " added" : ""
                  }`}
                  disabled={addedMain[singleItem?.sku_code]}
                  onClick={() =>
                    addToCart(
                      [singleItem.sku_code],
                      singleItem.product_name,
                      singleItem?.pQunatity ? singleItem.pQunatity : 1,
                      "",
                      false,
                      singleItem?.MandatoryProducts || [],
                      singleItem.products_id,
                      singleItem,
                      false,
                      singleItem.sku_code
                    )
                  }
                >
                  {addedMain[singleItem?.sku_code] ? "Added ✓" : "Add to Cart"}
                </button>
              </div>
            </div>

            {renderRelatedAccordion(
              "Related Products",
              singleItem?.child_sku,
              singleItem?.sku_code
            )}
          </>
        )}
      </div>

      <div className="atcFooter">
        <span className="atcItemsAdded">
          {addedCount} item{addedCount === 1 ? "" : "s"} added
        </span>
        <button type="button" className="atcDoneBtn" onClick={closeModal}>
          Done
        </button>
      </div>
    </div>
  );
};

export default Container;
